import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Play, CheckCircle, Clock, Users, Award, FileText, Target, Star, GraduationCap, Briefcase, Heart, Baby, Shield, Eye, MessageCircle, Sparkles, Building, UserCheck, ClipboardList, BookOpen, Palette, Home, Network, Handshake } from 'lucide-react';

// Couleurs pour le DEEJE - thème éducatif / petite enfance
const theme = {
  primary: '#6B4C9A',      // Violet éducatif
  secondary: '#F4A261',    // Orange chaleureux
  accent1: '#2A9D8F',      // Vert émeraude (DC1)
  accent2: '#E76F51',      // Corail (DC2)
  accent3: '#457B9D',      // Bleu professionnel (DC3)
  accent4: '#E9C46A',      // Jaune doré (DC4)
  background: '#FBF8FF',   // Fond très clair violet
  text: '#2C3E50',
  success: '#27AE60',
  error: '#E74C3C',
};

const slides = [
  // INTRODUCTION
  {
    id: 1,
    title: "Réussir sa VAE DEEJE",
    subtitle: "Diplôme d'État d'Éducateur de Jeunes Enfants",
    description: "Arrêté du 22 août 2018 - Nouvelle version",
    type: "title",
  },
  {
    id: 2,
    title: "Pourquoi choisir la VAE DEEJE ?",
    type: "benefits",
    items: [
      "Valoriser votre expertise éducative auprès des jeunes enfants",
      "Obtenir un diplôme d'État de niveau 6 (Bac+3)",
      "Accéder à des postes de responsabilité",
      "Contribuer au développement de la petite enfance",
      "Évoluer vers des fonctions de coordination ou direction"
    ]
  },
  {
    id: 3,
    title: "Conditions d'éligibilité",
    type: "eligibility",
    items: [
      "1 an d'expérience conseillé (1607h cumulées)",
      "Activités en lien avec l'éducation de jeunes enfants",
      "Expérience salariée, bénévole ou indépendante",
      "Travail en crèche, halte-garderie, RAM, PMI, hôpital..."
    ]
  },
  {
    id: 4,
    title: "Les 7 étapes de la VAE",
    type: "steps",
    items: [
      { num: 1, text: "Vérifier son éligibilité" },
      { num: 2, text: "Déposer le Livret 1" },
      { num: 3, text: "Rédiger le Livret 2" },
      { num: 4, text: "Se faire accompagner" },
      { num: 5, text: "Déposer le dossier" },
      { num: 6, text: "Passer l'oral" },
      { num: 7, text: "Décision du jury" }
    ]
  },
  {
    id: 5,
    title: "Le Livret 2 : 4 Domaines de Compétences",
    type: "dc-overview",
    domains: [
      { num: 1, title: "Accueil et accompagnement", subtitle: "du jeune enfant et de sa famille", color: theme.accent1 },
      { num: 2, title: "Action éducative", subtitle: "en direction du jeune enfant", color: theme.accent2 },
      { num: 3, title: "Travail en équipe", subtitle: "et communication professionnelle", color: theme.accent3 },
      { num: 4, title: "Dynamiques institutionnelles", subtitle: "partenariats et réseaux", color: theme.accent4 }
    ]
  },

  // DC1 - ACCUEIL ET ACCOMPAGNEMENT
  {
    id: 6,
    title: "DC1 – Accueil et accompagnement",
    subtitle: "du jeune enfant et de sa famille",
    type: "dc-title",
    color: theme.accent1,
    icon: "baby"
  },
  {
    id: 7,
    title: "1.1 à 1.3 – Pratiques éducatives",
    subtitle: "Mettre en œuvre des pratiques adaptées au contexte d'accueil",
    type: "competence",
    color: theme.accent1,
    items: [
      "Observer et écouter pour analyser les situations éducatives",
      "Reconnaître les potentialités de chaque enfant au sein du collectif",
      "Favoriser le développement global de l'enfant",
      "Concevoir les conditions favorisant le jeu et l'expression",
      "Favoriser l'accès à l'art et à la culture",
      "Contribuer à la prévention précoce autour des premiers liens"
    ]
  },
  {
    id: 8,
    title: "1.4 à 1.7 – Socialisation et relation",
    subtitle: "Accompagner les processus de socialisation",
    type: "competence",
    color: theme.accent1,
    items: [
      "Prendre en compte l'individualité au sein d'un collectif",
      "Créer et utiliser des outils d'observation et d'analyse",
      "Animer un groupe en fonction du contexte et de l'âge",
      "Gérer les conflits et tensions au sein du groupe",
      "Développer une relation privilégiée avec le jeune enfant",
      "Répondre aux besoins de sécurité physique, affective et psychologique"
    ]
  },
  {
    id: 9,
    title: "1.8 à 1.11 – Accueil des familles",
    subtitle: "Mettre en œuvre des pratiques d'accueil adaptées",
    type: "competence",
    color: theme.accent1,
    items: [
      "Instaurer un lien de confiance avec l'enfant et la famille",
      "Repérer les situations à risque et de maltraitance",
      "Construire une cohérence éducative entre famille et structure",
      "Observer et analyser la relation enfants-parents",
      "Reconnaître et faciliter la fonction parentale",
      "Orienter les familles vers les services compétents"
    ]
  },
  {
    id: 10,
    title: "1.12 à 1.17 – Coopération parentale",
    subtitle: "Impliquer les parents dans la vie de la structure",
    type: "competence",
    color: theme.accent1,
    items: [
      "Organiser la transmission d'informations pour la continuité des soins",
      "Favoriser la présence des familles au sein de la structure",
      "Créer les conditions de participation aux instances institutionnelles",
      "Coopérer avec les parents pour le projet éducatif",
      "Construire une continuité éducative famille/institution",
      "Concevoir des actions favorisant la participation des parents"
    ]
  },
  {
    id: 11,
    title: "1.18 à 1.19 – Organisation des espaces",
    subtitle: "Aménager des espaces sécurisants et éducatifs",
    type: "competence",
    color: theme.accent1,
    items: [
      "Aménager un environnement sécurisant pour l'exploration",
      "Créer des espaces éducatifs et ludiques adaptés",
      "Favoriser les capacités d'action de l'enfant dans un collectif",
      "Concevoir des espaces d'échange entre parents et professionnels",
      "Co-construire des réponses aux questionnements éducatifs"
    ]
  },

  // DC2 - ACTION ÉDUCATIVE
  {
    id: 12,
    title: "DC2 – Action éducative",
    subtitle: "en direction du jeune enfant",
    type: "dc-title",
    color: theme.accent2,
    icon: "palette"
  },
  {
    id: 13,
    title: "2.1 à 2.3 – Situations éducatives",
    subtitle: "Concevoir des situations adaptées aux besoins",
    type: "competence",
    color: theme.accent2,
    items: [
      "Identifier les besoins, capacités et centres d'intérêts",
      "Concevoir des outils éducatifs et ludiques innovants",
      "Proposer des médiations favorisant la créativité",
      "Initier les enfants aux pratiques artistiques et culturelles",
      "Organiser des repères spatio-temporels adaptés",
      "Observer les interactions dans le groupe"
    ]
  },
  {
    id: 14,
    title: "2.4 à 2.6 – Prévention et conduite",
    subtitle: "Actions de prévention et travail en équipe",
    type: "competence",
    color: theme.accent2,
    items: [
      "Identifier et analyser les besoins de prévention",
      "Mettre en place des pratiques éducatives de soins",
      "Assurer les protocoles d'hygiène et de sécurité",
      "Impulser l'élaboration et la conduite de l'action éducative",
      "Promouvoir les valeurs et le projet de la structure",
      "Travailler en équipe pluriprofessionnelle"
    ]
  },
  {
    id: 15,
    title: "2.7 à 2.9 – Coordination et évaluation",
    subtitle: "Coordonner et évaluer l'action éducative",
    type: "competence",
    color: theme.accent2,
    items: [
      "Impulser une dynamique au sein de l'équipe",
      "Mobiliser les ressources pour l'action éducative",
      "Accompagner l'équipe dans la mise en œuvre",
      "Initier une démarche d'évaluation",
      "Créer des outils d'évaluation et de suivi",
      "Concevoir des actions de prévention et communication"
    ]
  },

  // DC3 - TRAVAIL EN ÉQUIPE
  {
    id: 16,
    title: "DC3 – Travail en équipe",
    subtitle: "pluriprofessionnelle et communication professionnelle",
    type: "dc-title",
    color: theme.accent3,
    icon: "users"
  },
  {
    id: 17,
    title: "3.1 à 3.4 – Projet et réunions",
    subtitle: "Contribuer au projet et organiser les réunions",
    type: "competence",
    color: theme.accent3,
    items: [
      "Différencier et articuler les différents projets",
      "Participer à l'élaboration du projet social et éducatif",
      "Utiliser la méthodologie de projet",
      "Définir des objectifs de réunion",
      "Organiser et animer des réunions",
      "Créer des supports de communication adaptés"
    ]
  },
  {
    id: 18,
    title: "3.5 à 3.8 – Écrits et équipe",
    subtitle: "Rédiger des écrits et s'inscrire dans l'équipe",
    type: "competence",
    color: theme.accent3,
    items: [
      "Identifier et élaborer les différents types d'écrits",
      "Rédiger des écrits adaptés aux destinataires",
      "Identifier sa place et articuler ses interventions",
      "Contribuer à la cohérence de l'action éducative",
      "Mener des actions d'information et sensibilisation",
      "Appréhender et mobiliser l'environnement numérique"
    ]
  },

  // DC4 - DYNAMIQUES INSTITUTIONNELLES
  {
    id: 19,
    title: "DC4 – Dynamiques institutionnelles",
    subtitle: "partenariats et réseaux",
    type: "dc-title",
    color: theme.accent4,
    icon: "network"
  },
  {
    id: 20,
    title: "4.1 à 4.4 – Pratiques et missions",
    subtitle: "Analyser les pratiques et représenter l'institution",
    type: "competence",
    color: theme.accent4,
    items: [
      "Questionner et ajuster ses pratiques professionnelles",
      "Organiser des temps d'analyse de pratiques",
      "Se documenter et actualiser ses connaissances",
      "Contribuer à des travaux de recherche",
      "Décliner les orientations et missions de la structure",
      "Représenter le service, l'établissement, l'institution"
    ]
  },
  {
    id: 21,
    title: "4.5 à 4.9 – Partenariats et veille",
    subtitle: "Favoriser les dynamiques partenariales",
    type: "competence",
    color: theme.accent4,
    items: [
      "Identifier les partenaires pour l'action éducative",
      "Communiquer sur les actions éducatives et culturelles",
      "Participer à l'animation et dynamique de réseau",
      "Développer une veille sur les politiques petite enfance",
      "Contribuer à des projets de développement d'accueil",
      "Conduire des groupes de travail thématiques"
    ]
  },

  // CONSEILS ET ORAL
  {
    id: 22,
    title: "Conseils pour réussir",
    type: "tips",
    tips: [
      "Décrire des situations éducatives concrètes",
      "Analyser votre posture professionnelle",
      "Justifier vos choix pédagogiques",
      "Montrer votre réflexion éducative"
    ],
    example: {
      title: "Exemple de formulation attendue",
      wrong: "« J'organise des activités pour les enfants »",
      right: "« Je conçois une activité d'éveil sensoriel en analysant les besoins développementaux de chaque enfant et en favorisant leur expression créative au sein du groupe. »"
    }
  },
  {
    id: 23,
    title: "Pourquoi se faire accompagner ?",
    type: "accompagnement",
    items: [
      "Structurer ses écrits selon les 4 DC",
      "Comprendre les attentes du jury DEEJE",
      "Préparer l'oral (entretien + mise en situation)",
      "Maximiser ses chances de validation totale"
    ]
  },
  {
    id: 24,
    title: "L'oral devant le jury",
    type: "oral",
    details: [
      { label: "Durée totale", value: "1 heure" },
      { label: "Présentation personnelle", value: "10 minutes" },
      { label: "Échanges avec le jury", value: "50 minutes" }
    ],
    note: "Le jury évalue vos compétences sur les 4 domaines à travers l'entretien basé sur votre livret 2."
  },
  {
    id: 25,
    title: "Décision du jury",
    type: "decision",
    decisions: [
      { status: "success", text: "Validation totale", desc: "Vous obtenez votre diplôme DEEJE !" },
      { status: "partial", text: "Validation partielle", desc: "Un ou plusieurs DC à compléter" },
      { status: "retry", text: "Refus", desc: "Possibilité de retravailler le dossier" }
    ]
  },
  {
    id: 26,
    title: "Planning indicatif",
    type: "timeline",
    phases: [
      { phase: "Livret 1", duration: "2 mois" },
      { phase: "Livret 2", duration: "8 à 12 mois" },
      { phase: "Jury", duration: "2 à 4 mois" }
    ],
    total: "Durée totale : 12 à 18 mois"
  },
  {
    id: 27,
    title: "Débouchés professionnels",
    type: "careers",
    jobs: [
      "Éducateur de jeunes enfants",
      "Responsable de structure",
      "Coordinateur petite enfance",
      "Directeur de crèche",
      "Référent technique RAM",
      "Formateur petite enfance"
    ]
  },
  {
    id: 28,
    title: "En résumé",
    type: "summary",
    points: [
      "La VAE DEEJE valorise votre expertise éducative",
      "4 domaines de compétences à maîtriser",
      "Un accompagnement personnalisé = réussite"
    ]
  }
];

const getIcon = (iconName, size = 50) => {
  switch (iconName) {
    case 'baby': return <Baby size={size} />;
    case 'palette': return <Palette size={size} />;
    case 'users': return <Users size={size} />;
    case 'network': return <Network size={size} />;
    default: return <Award size={size} />;
  }
};

const SlideContent = ({ slide }) => {
  switch (slide.type) {
    case "title":
      return (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem 2rem',
          background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.accent1} 100%)`,
          borderRadius: '1rem',
          color: 'white'
        }}>
          <div style={{ 
            width: '100px', height: '100px', borderRadius: '50%',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 2rem', border: '3px solid rgba(255,255,255,0.3)'
          }}>
            <GraduationCap size={50} style={{ color: 'white' }} />
          </div>
          <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'white' }}>
            {slide.title}
          </h2>
          <p style={{ fontSize: '1.25rem', marginBottom: '0.5rem', opacity: 0.95 }}>{slide.subtitle}</p>
          <p style={{ fontSize: '1rem', opacity: 0.85, fontStyle: 'italic' }}>{slide.description}</p>
        </div>
      );

    case "benefits":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 'bold', marginBottom: '1.5rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxWidth: '650px', margin: '0 auto' }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.9rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderLeft: `4px solid ${theme.secondary}`
              }}>
                <Star size={18} style={{ color: theme.secondary, flexShrink: 0 }} />
                <span style={{ fontSize: '0.95rem', color: theme.text }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "eligibility":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 'bold', marginBottom: '1.5rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxWidth: '600px', margin: '0 auto' }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.9rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderLeft: `4px solid ${theme.accent1}`
              }}>
                <CheckCircle size={20} style={{ color: theme.accent1, flexShrink: 0 }} />
                <span style={{ fontSize: '0.95rem', color: theme.text }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "steps":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 'bold', marginBottom: '1.5rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.6rem', maxWidth: '750px', margin: '0 auto' }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0.9rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', textAlign: 'center'
              }}>
                <div style={{
                  width: '38px', height: '38px', borderRadius: '50%', background: theme.primary,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.5rem',
                  color: 'white', fontWeight: 'bold', fontSize: '1rem'
                }}>{item.num}</div>
                <span style={{ fontSize: '0.85rem', color: theme.text }}>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "dc-overview":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 'bold', marginBottom: '1.5rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxWidth: '650px', margin: '0 auto' }}>
            {slide.domains.map((domain, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem',
                background: domain.color, borderRadius: '0.75rem', color: 'white'
              }}>
                <div style={{
                  width: '45px', height: '45px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '1.2rem', flexShrink: 0
                }}>DC{domain.num}</div>
                <div>
                  <h4 style={{ fontWeight: 'bold', marginBottom: '0.2rem', fontSize: '1rem' }}>{domain.title}</h4>
                  <span style={{ fontSize: '0.85rem', opacity: 0.9 }}>{domain.subtitle}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      );

    case "dc-title":
      return (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem 2rem',
          background: slide.color,
          borderRadius: '1rem',
          color: 'white'
        }}>
          <div style={{ 
            width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem'
          }}>
            {getIcon(slide.icon, 40)}
          </div>
          <h2 style={{ fontSize: '1.7rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{slide.title}</h2>
          <p style={{ fontSize: '1.1rem', opacity: 0.9 }}>{slide.subtitle}</p>
        </div>
      );

    case "competence":
      return (
        <div style={{ padding: '1rem' }}>
          <div style={{ 
            background: slide.color, color: 'white', padding: '0.9rem 1.25rem', 
            borderRadius: '0.75rem 0.75rem 0 0'
          }}>
            <h2 style={{ fontSize: '1.15rem', fontWeight: 'bold', marginBottom: '0.2rem' }}>{slide.title}</h2>
            {slide.subtitle && <p style={{ fontSize: '0.9rem', opacity: 0.9 }}>{slide.subtitle}</p>}
          </div>
          <div style={{ 
            background: 'white', borderRadius: '0 0 0.75rem 0.75rem', 
            padding: '0.9rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.5rem 0',
                borderBottom: idx < slide.items.length - 1 ? '1px solid #eee' : 'none'
              }}>
                <div style={{
                  width: '6px', height: '6px', borderRadius: '50%', background: slide.color,
                  marginTop: '0.45rem', flexShrink: 0
                }} />
                <span style={{ fontSize: '0.88rem', color: theme.text, lineHeight: '1.4' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "tips":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.25rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxWidth: '600px', margin: '0 auto 1rem' }}>
            {slide.tips.map((tip, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.6rem',
                background: 'white', borderRadius: '0.5rem', boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
              }}>
                <Target size={16} style={{ color: theme.secondary, flexShrink: 0 }} />
                <span style={{ fontSize: '0.9rem', color: theme.text }}>{tip}</span>
              </div>
            ))}
          </div>
          <div style={{
            maxWidth: '600px', margin: '0 auto', padding: '0.9rem',
            background: '#f8f9fa', borderRadius: '0.75rem', border: `2px solid ${theme.accent3}`
          }}>
            <p style={{ fontWeight: 'bold', marginBottom: '0.6rem', color: theme.primary, textAlign: 'center', fontSize: '0.9rem' }}>
              {slide.example.title}
            </p>
            <div style={{ padding: '0.5rem 0.7rem', background: '#ffebee', borderRadius: '0.5rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ color: theme.error, fontWeight: 'bold' }}>✗</span>
              <span style={{ color: theme.error, fontSize: '0.85rem' }}>{slide.example.wrong}</span>
            </div>
            <div style={{ padding: '0.5rem 0.7rem', background: '#e8f5e9', borderRadius: '0.5rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
              <span style={{ color: theme.success, fontWeight: 'bold' }}>✓</span>
              <span style={{ color: theme.success, fontSize: '0.85rem' }}>{slide.example.right}</span>
            </div>
          </div>
        </div>
      );

    case "accompagnement":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 'bold', marginBottom: '1.5rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxWidth: '500px', margin: '0 auto' }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.9rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderLeft: `4px solid ${theme.accent1}`
              }}>
                <CheckCircle size={20} style={{ color: theme.success, flexShrink: 0 }} />
                <span style={{ fontSize: '0.95rem', color: theme.text }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "oral":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 'bold', marginBottom: '1.5rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ maxWidth: '500px', margin: '0 auto', background: 'white', borderRadius: '1rem', padding: '1.25rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
            {slide.details.map((detail, idx) => (
              <div key={idx} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.65rem',
                background: theme.background, borderRadius: '0.5rem', marginBottom: '0.6rem'
              }}>
                <span style={{ fontWeight: 'bold', color: theme.text, fontSize: '0.95rem' }}>{detail.label}</span>
                <span style={{ color: theme.primary, fontWeight: 'bold' }}>{detail.value}</span>
              </div>
            ))}
            <div style={{ padding: '0.65rem', background: theme.accent3 + '25', borderRadius: '0.5rem' }}>
              <p style={{ fontSize: '0.88rem', color: theme.text, fontStyle: 'italic' }}>{slide.note}</p>
            </div>
          </div>
        </div>
      );

    case "decision":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 'bold', marginBottom: '1.5rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxWidth: '500px', margin: '0 auto' }}>
            {slide.decisions.map((decision, idx) => (
              <div key={idx} style={{
                padding: '0.9rem',
                background: decision.status === 'success' ? '#e8f5e9' : decision.status === 'partial' ? '#fff3e0' : '#ffebee',
                borderRadius: '0.75rem',
                borderLeft: `4px solid ${decision.status === 'success' ? theme.success : decision.status === 'partial' ? '#ff9800' : theme.error}`
              }}>
                <h4 style={{ fontWeight: 'bold', marginBottom: '0.2rem', fontSize: '1rem', color: decision.status === 'success' ? theme.success : decision.status === 'partial' ? '#e65100' : theme.error }}>
                  {decision.text}
                </h4>
                <span style={{ fontSize: '0.88rem', color: theme.text }}>{decision.desc}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "timeline":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 'bold', marginBottom: '1.5rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxWidth: '500px', margin: '0 auto 1.25rem' }}>
            {slide.phases.map((phase, idx) => (
              <div key={idx} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.9rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <Clock size={20} style={{ color: theme.primary }} />
                  <span style={{ fontWeight: 'bold', color: theme.text, fontSize: '0.95rem' }}>{phase.phase}</span>
                </div>
                <span style={{ fontWeight: 'bold', color: theme.primary }}>{phase.duration}</span>
              </div>
            ))}
          </div>
          <div style={{ padding: '0.9rem', background: theme.primary, borderRadius: '0.75rem', textAlign: 'center', maxWidth: '500px', margin: '0 auto' }}>
            <span style={{ fontWeight: 'bold', color: 'white', fontSize: '1.05rem' }}>{slide.total}</span>
          </div>
        </div>
      );

    case "careers":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 'bold', marginBottom: '1.5rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.6rem', maxWidth: '650px', margin: '0 auto' }}>
            {slide.jobs.map((job, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}>
                <Briefcase size={18} style={{ color: theme.primary, flexShrink: 0 }} />
                <span style={{ fontSize: '0.88rem', color: theme.text }}>{job}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "summary":
      return (
        <div style={{ 
          padding: '2rem',
          background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.accent1} 100%)`,
          borderRadius: '1rem',
          textAlign: 'center'
        }}>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 'bold', marginBottom: '1.5rem', color: 'white' }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxWidth: '500px', margin: '0 auto' }}>
            {slide.points.map((point, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem', padding: '0.7rem',
                background: 'rgba(255,255,255,0.15)', borderRadius: '0.75rem'
              }}>
                <CheckCircle size={18} style={{ color: 'white', flexShrink: 0 }} />
                <span style={{ color: 'white', fontSize: '0.95rem' }}>{point}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '1.5rem', padding: '0.9rem', background: 'rgba(255,255,255,0.9)', borderRadius: '0.75rem' }}>
            <p style={{ color: theme.primary, fontWeight: 'bold', fontSize: '0.95rem' }}>
              Lancez-vous dans l'aventure VAE DEEJE avec notre accompagnement personnalisé !
            </p>
          </div>
        </div>
      );

    default:
      return null;
  }
};

const DEEJEPresentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  React.useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setCurrentSlide((prev) => {
          if (prev === slides.length - 1) { setIsPlaying(false); return prev; }
          return prev + 1;
        });
      }, 6000);
    }
    return () => clearInterval(interval);
  }, [isPlaying]);

  return (
    <div style={{ borderRadius: '1rem', overflow: 'hidden', background: theme.background, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} data-testid="deeje-presentation">
      <div style={{ minHeight: '430px', padding: '1.25rem', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <SlideContent slide={slides[currentSlide]} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.65rem 1.25rem', borderTop: `1px solid ${theme.accent1}30`, background: 'white' }}>
        <button onClick={prevSlide} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 0.9rem', border: `1px solid ${theme.accent1}`, borderRadius: '0.5rem', background: 'white', cursor: 'pointer', color: theme.text, fontWeight: '500', fontSize: '0.9rem' }} data-testid="prev-slide-btn">
          <ChevronLeft size={16} /><span>Précédent</span>
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <button onClick={() => setIsPlaying(!isPlaying)} style={{ width: '38px', height: '38px', borderRadius: '50%', border: 'none', background: theme.primary, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }} data-testid="play-pause-btn">
            {isPlaying ? '⏸' : <Play size={14} />}
          </button>
          <span style={{ fontWeight: 'bold', color: theme.primary, fontSize: '0.9rem' }}>{currentSlide + 1} / {slides.length}</span>
        </div>
        <button onClick={nextSlide} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 0.9rem', border: 'none', borderRadius: '0.5rem', background: theme.primary, cursor: 'pointer', color: 'white', fontWeight: '500', fontSize: '0.9rem' }} data-testid="next-slide-btn">
          <span>Suivant</span><ChevronRight size={16} />
        </button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '0.25rem', padding: '0.6rem', background: 'white', flexWrap: 'wrap' }}>
        {slides.map((_, idx) => (
          <button key={idx} onClick={() => setCurrentSlide(idx)} style={{ width: '7px', height: '7px', borderRadius: '50%', border: 'none', background: idx === currentSlide ? theme.primary : theme.accent1, cursor: 'pointer', opacity: idx === currentSlide ? 1 : 0.35 }} data-testid={`slide-dot-${idx}`} />
        ))}
      </div>
    </div>
  );
};

export default DEEJEPresentation;
